import type { DataProvider } from '@refinedev/core'

/**
 * Data provider custom pour Refine.
 * Mappe les conventions Refine (_start/_end, _sort/_order, x-total-count)
 * vers l'API Express/Prisma existante.
 */
export const dataProvider = (apiUrl: string): DataProvider => ({
  getList: async ({ resource, pagination, sorters, filters }) => {
    const { currentPage = 1, pageSize = 10 } = pagination ?? {}
    const params = new URLSearchParams()
    params.set('_start', String((currentPage - 1) * pageSize))
    params.set('_end', String(currentPage * pageSize))

    if (sorters?.[0]) {
      params.set('_sort', sorters[0].field)
      params.set('_order', sorters[0].order)
    }

    filters?.forEach((f) => {
      if ('field' in f && f.value !== undefined && f.value !== null && f.value !== '') {
        params.set(f.field, String(f.value))
      }
    })

    const res = await fetch(`${apiUrl}/${resource}?${params}`, {
      credentials: 'include',
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: `HTTP ${res.status}` }))
      throw new Error(error.message || error.error || `Erreur ${res.status}`)
    }

    const data = await res.json()
    const total = parseInt(res.headers.get('x-total-count') || String(data.length), 10)

    return { data, total }
  },

  getOne: async ({ resource, id }) => {
    const res = await fetch(`${apiUrl}/${resource}/${id}`, {
      credentials: 'include',
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: `HTTP ${res.status}` }))
      throw new Error(error.message || error.error || `Erreur ${res.status}`)
    }

    return { data: await res.json() }
  },

  update: async ({ resource, id, variables }) => {
    const res = await fetch(`${apiUrl}/${resource}/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(variables),
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: `HTTP ${res.status}` }))
      throw new Error(error.message || error.error || `Erreur ${res.status}`)
    }

    return { data: await res.json() }
  },

  custom: async ({ url, method, payload }) => {
    const res = await fetch(url!, {
      method: method as string,
      credentials: 'include',
      headers: payload ? { 'Content-Type': 'application/json' } : undefined,
      body: payload ? JSON.stringify(payload) : undefined,
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: `HTTP ${res.status}` }))
      throw new Error(error.message || error.error || `Erreur ${res.status}`)
    }

    return { data: await res.json() }
  },

  getApiUrl: () => apiUrl,

  create: async ({ resource, variables }) => {
    const res = await fetch(`${apiUrl}/${resource}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(variables),
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: `HTTP ${res.status}` }))
      throw new Error(error.message || error.error || `Erreur ${res.status}`)
    }

    return { data: await res.json() }
  },

  deleteOne: async ({ resource, id }) => {
    const res = await fetch(`${apiUrl}/${resource}/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    })

    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: `HTTP ${res.status}` }))
      throw new Error(error.message || error.error || `Erreur ${res.status}`)
    }

    return { data: await res.json() }
  },
})
